<template>
  <div class="p-3">
    <template v-if="organisation">
      <c-input v-model="newGroupName" style="width: 300px" type="text" placeholder="New group name">
        <template #append><c-button type="submit" color="primary" @click="addGroup">Add Group</c-button></template>
      </c-input>

      <c-row class="mt-5">
        <c-col sm="12" md="4">
          <template v-if="hasGroups">
            <table class="table">
              <thead>
                <tr>
                  <th>Group name</th>
                  <th class="text-center">Sites</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="group in groups">
                  <td>
                    <a
                      :class="`${selectedGroup && (selectedGroup._id === group._id ? 'selected' : '')} cursor-pointer`"
                      @click="selectGroup(group)"
                    >
                      {{ group.name }}
                    </a>
                  </td>
                  <td class="text-center">{{ group.sites.length }}</td>
                </tr>
              </tbody>
            </table>
          </template>
          <template v-else>
            <p class="lead" style="">No groups have been created.</p>
          </template>
        </c-col>
        <c-col sm="12" md="8">
          <template v-if="selectedGroup">
            <c-row>
              <c-col>
                <h2>{{ selectedGroup.name }}</h2>
              </c-col>
              <c-col>
                <c-button-group class="float-right">
                  <c-button size="sm" color="primary" @click="showRenameGroup = true">Edit group</c-button>
                  <c-button size="sm" color="danger" @click="showGroupDeleteModal = true">Delete group</c-button>
                </c-button-group>
              </c-col>
            </c-row>

            <hr />
            <template v-if="sites.length">
              <c-select
                style="width: 300px"
                :value="this.selectedSiteId"
                placeholder="Select site"
                :options="sitesToAdd"
                @update:value="
                  e => {
                    this.selectedSiteId = e;
                  }
                "
              >
                <template #append>
                  <c-button type="submit" color="primary" @click="addSite">Add Site</c-button>
                </template>
              </c-select>
            </template>
            <template v-else>
              <p class="lead" style="padding: 50px; width: 100%; text-align: center">
                >No sites have been added to your organisation.
              </p>
            </template>

            <template v-if="selectedGroup.sites.length">
              <table class="table">
                <thead>
                  <tr>
                    <th>Site</th>
                    <th>Address</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="site in selectedGroup.siteData">
                    <td v-html="site.name" />
                    <td>{{ site.siteAddress }}</td>
                    <td>
                      <c-button size="sm" color="danger" class="float-right" @click="showDeleteSite(site._id)">
                        Remove
                      </c-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
            <template v-else>
              <p class="lead" style="padding: 50px; width: 100%; text-align: center">
                No sites have been added to this group.
                <br />
                <br />
                Please use the option above to add a site.
              </p>
            </template>
          </template>
        </c-col>
      </c-row>
    </template>

    <c-modal title="Delete group" size="" :show="showGroupDeleteModal" @update:show="showGroupDeleteModal = $event">
      <p class="lead">Delete this group? This will remove the group from all users. Cannot undo.</p>
      <template v-slot:footer>
        <c-button-group>
          <c-button color="danger" @click="removeGroup">
            Delete
          </c-button>
          <c-button color="secondary" @click="showGroupDeleteModal = false">Cancel</c-button>
        </c-button-group>
      </template>
    </c-modal>

    <c-modal title="Remove site" size="" :show="showSiteDeleteModal" @update:show="showSiteDeleteModal = $event">
      <p class="lead">Remove this site from the group?</p>
      <template v-slot:footer>
        <c-button-group>
          <c-button color="danger" @click="removeSite">
            Remove
          </c-button>
          <c-button color="secondary" @click="showSiteDeleteModal = false">Cancel</c-button>
        </c-button-group>
      </template>
    </c-modal>

    <c-modal title="Rename group" size="" :show="showRenameGroup" @update:show="showRenameGroup = $event">
      <c-input v-model="updatedGroupName" type="text" />

      <template v-slot:footer>
        <c-button-group>
          <c-button color="primary" @click="updateGroupName">
            Rename
          </c-button>
          <c-button color="secondary" @click="showRenameGroup = false">Cancel</c-button>
        </c-button-group>
      </template>
    </c-modal>
  </div>
</template>

<script>
import _ from 'lodash';
import { buildEndpoint } from '@/libs/buildEndPoint';

export default {
  props: {
    organisation: Object,
  },
  data() {
    return {
      error: null,
      newGroupName: '',
      updatedGroupName: '',
      isAddingGroup: false,
      baseEndPoint: 'organisation-admin/organisation/groups',
      selectedGroup: null,
      selectedSiteId: null,
      groups: [],
      sites: [],
      showGroupDeleteModal: false,
      showSiteDeleteModal: false,
      siteIdToDelete: null,
      showRenameGroup: false,
    };
  },
  computed: {
    hasGroups() {
      return _.isArray(this.groups) && this.groups.length;
    },
    sitesToAdd() {
      const sites = this.sites
        .map((site) => ({
          value: site._id,
          label: site.name,
        }))
        .filter((site) => !this.selectedGroup.sites.includes(site.value));
      sites.unshift({
        value: null,
        label: 'Select site',
      });
      return sites;
    },
  },
  created() {
    this.groups = this.sortGroups(this.organisation.groups);
    this.getAllSites(() => {
      this.updateSiteData();
    });
  },
  methods: {
    getSiteData(siteId) {
      const site = _.find(this.sites, { _id: siteId });
      return site || {};
    },
    getAllSites(callback) {
      this.$http
        .get(buildEndpoint('organisation-admin/organisation/get-all-sites'), {})
        .then((response) => {
          this.isAddingGroup = false;
          if (response.data.success) {
            this.sites = response.data.data;
          } else {
            this.error = response.data.message;
          }
          return callback();
        })
        .catch((error) => {
          this.isAddingGroup = false;
          this.error = error;
          return callback();
        });
    },
    sortGroups(groups) {
      return _.sortBy(groups, (group) => group.name.toLowerCase());
    },
    updateSiteData() {
      this.groups.forEach((group) => {
        group.siteData = [];
        group.sites.forEach((siteId) => {
          const siteData = this.getSiteData(siteId);
          group.siteData.push({
            _id: siteId,
            name: siteData.name,
            siteAddress: siteData.siteAddress,
          });
        });
      });
    },
    getAllGroups(callback) {
      this.$http
        .get(buildEndpoint(`${this.baseEndPoint}/get-all`), {})
        .then((response) => {
          if (response.data.success) {
            this.groups = this.sortGroups(response.data.data.groups);
            this.updateSiteData();
          } else {
            this.error = response.data.message;
          }
          return callback();
        })
        .catch((error) => {
          this.error = error;
          return callback();
        });
    },
    updateSelectedGroup() {
      this.selectedGroup = _.find(this.groups, { _id: this.selectedGroup._id });
    },
    addGroup() {
      if (this.newGroupName) {
        this.isAddingGroup = true;
        this.error = null;

        const data = {
          groupName: this.newGroupName,
          organisationId: this.organisation._id,
        };

        this.$http
          .post(buildEndpoint(`${this.baseEndPoint}/add`), data)
          .then((response) => {
            this.isAddingGroup = false;
            if (response.data.success) {
              // Refresh groups
              this.getAllGroups(() => {
                // Do nothing
              });
              this.newGroupName = '';
            } else {
              this.error = response.data.message;
            }
          })
          .catch((error) => {
            this.isAddingGroup = false;
            this.error = error;
          });
      }
    },
    updateGroupName() {
      if (this.updatedGroupName) {
        this.error = null;

        const data = {
          groupName: this.updatedGroupName,
          groupId: this.selectedGroup._id,
        };

        this.$http
          .post(buildEndpoint(`${this.baseEndPoint}/update`), data)
          .then((response) => {
            if (response.data.success) {
              this.showRenameGroup = false;
              this.selectedGroup.name = this.updatedGroupName;
            } else {
              this.error = response.data.message;
            }
          })
          .catch((error) => {
            this.error = error;
          });
      }
    },
    removeGroup() {
      if (this.selectedGroup) {
        this.error = null;

        const data = {
          groupId: this.selectedGroup._id,
          organisationId: this.organisation._id,
        };

        this.$http
          .post(buildEndpoint(`${this.baseEndPoint}/remove`), data)
          .then((response) => {
            if (response.data.success) {
              this.selectedGroup = null;
              this.showGroupDeleteModal = false;
              // Refresh groups
              this.getAllGroups(() => {
                // Do nothing
              });
            } else {
              this.error = response.data.message;
            }
          })
          .catch((error) => {
            // this.isAddingGroup = false
            this.error = error;
          });
      }
    },
    selectGroup(group) {
      this.selectedGroup = group;
      this.updatedGroupName = group.name;
      this.selectedSiteId = null;
    },
    showDeleteSite(siteId) {
      this.showSiteDeleteModal = true;
      this.siteIdToDelete = siteId;
    },
    addSite() {
      if (this.selectedSiteId) {
        if (this.selectedGroup.sites.indexOf(this.selectedSiteId) !== -1) {
          return;
        }

        this.error = null;

        const data = {
          groupId: this.selectedGroup._id,
          siteId: this.selectedSiteId,
        };

        this.$http
          .post(buildEndpoint(`${this.baseEndPoint}/add-site`), data)
          .then((response) => {
            if (response.data.success) {
              this.selectedSiteId = null;
              // Refresh groups
              this.getAllGroups(() => {
                this.updateSelectedGroup();
              });
            } else {
              this.error = response.data.message;
            }
          })
          .catch((error) => {
            this.error = error;
          });
      }
    },
    removeSite() {
      if (this.siteIdToDelete) {
        this.error = null;
        this.showSiteDeleteModal = false;

        const data = {
          groupId: this.selectedGroup._id,
          organisationId: this.organisation._id,
          siteId: this.siteIdToDelete,
        };

        this.$http
          .post(buildEndpoint(`${this.baseEndPoint}/remove-site`), data)
          .then((response) => {
            this.siteIdToDelete = null;
            if (response.data.success) {
              // Refresh groups
              this.getAllGroups(() => {
                this.updateSelectedGroup();
              });
            } else {
              this.error = response.data.message;
            }
          })
          .catch((error) => {
            this.error = error;
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped="">
table {
  td {
    a {
      &.selected {
        font-weight: bold;
      }
    }
  }
}
</style>
